import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useAccessControl, canAddGroupMember } from '../../hooks/useAccessControl';
import { isSuperAdmin, canManageGroups } from '../../utils/roles';
import { realtimeSyncService } from '../../services/realtimeSyncService';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import GroupInfoCard from './components/GroupInfoCard';
import ContributionScheduleCard from './components/ContributionScheduleCard';
import ContributionSettingsCard from './components/ContributionSettingsCard';
import MemberManagementTable from './components/MemberManagementTable';
import VerificationSettingsCard from './components/VerificationSettingsCard';
import AdvancedSettingsCard from './components/AdvancedSettingsCard';
import SubscriptionManagement from './components/SubscriptionManagement';
import LoanSettingsCard from './components/LoanSettingsCard';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import CreateGroupModal from './components/CreateGroupModal';
import RegisterMemberModal from './components/RegisterMemberModal';
import ImportMembersModal from './components/ImportMembersModal';
import PaymentWarningBanner from '../../components/ui/PaymentWarningBanner';
import { loanService } from '../../services/loanService';
import {
  GroupSettings,
  ContributionSchedule,
  GroupMember,
  VerificationSettings,
  DataConflictSettings,
  ExportPermissions,
  NotificationSettings,
} from
  './types';

const GroupManagement = () => {
  const { user, userProfile } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isRegisterMemberModalOpen, setIsRegisterMemberModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isRegisteringMember, setIsRegisteringMember] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentGroupId, setCurrentGroupId] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string>('');
  const [allGroups, setAllGroups] = useState<Array<{
    id: string;
    name: string;
    description: string;
    memberCount: number;
    isActive: boolean;
    createdAt: string;
    createdBy?: string;
    creatorName?: string;
    companyName?: string;
    companyLogoUrl?: string;
    creatorCompanyLogoUrl?: string;
  }>>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  const isSuperAdminUser = isSuperAdmin(userProfile?.role);
  const isGroupAdmin = userProfile?.role === 'group_admin';
  const isGroupCreator = userProfile?.role === 'group_creator';
  const hasAssignedGroupAdminRole = Boolean(
    userProfile?.group_roles?.some((r: any) => r === 'admin' || r === 'clerk')
  );
  const canSeeCashInHand = isSuperAdminUser || isGroupAdmin || isGroupCreator || hasAssignedGroupAdminRole;

  // Only group_creator, group_admin, or super_admin can create groups.
  // Assigned admins (group_members.role='admin'/'clerk') must NOT be able to create groups.
  const canCreateGroup = canManageGroups(userProfile?.role);

  // Safety: if role changes while modal is open (or stale UI), close it.
  useEffect(() => {
    if (!canCreateGroup && isCreateModalOpen) setIsCreateModalOpen(false);
  }, [canCreateGroup, isCreateModalOpen]);

  // Check access level for the current group
  const { accessLevel, loading: accessLoading } = useAccessControl(currentGroupId || undefined);

  const [groupSettings, setGroupSettings] = useState<GroupSettings>({
    id: '',
    name: '',
    description: '',
    createdAt: new Date(),
    memberCount: 0,
    totalContributions: 0
  });

  const [contributionSchedule, setContributionSchedule] = useState<ContributionSchedule>({
    id: '',
    days: [],
    time: '09:00',
    timezone: 'Pacific/Port_Vila',
    reminderEnabled: false,
    reminderTime: '08:00'
  });

  const [members, setMembers] = useState<GroupMember[]>([]);

  const [verificationSettings, setVerificationSettings] = useState<VerificationSettings>({
    requireAdminVerification: true,
    requireSecondUserConfirmation: false,
    allowMemberEdits: true,
    autoApproveContributions: false
  });

  const [conflictSettings, setConflictSettings] = useState<DataConflictSettings>({
    resolutionStrategy: 'admin-override',
    notifyOnConflict: true,
    autoResolveMinor: false
  });

  const [exportPermissions, setExportPermissions] = useState<ExportPermissions>({
    allowMemberExport: true,
    allowContributionExport: true,
    allowAuditLogExport: false,
    exportFormat: 'csv'
  });

  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>({
    contributionReminders: true,
    recordingAvailability: true,
    approvalNotifications: true,
    memberJoinNotifications: true
  });

  const [contributionSettings, setContributionSettings] = useState<{
    minContributionAmount: number | null;
    maxContributionAmount: number | null;
  }>({
    minContributionAmount: null,
    maxContributionAmount: null,
  });

  // Handle hash-based navigation
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      setActiveSection(hash);
    };

    // Set initial section from hash
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Fetch group data from Supabase
  useEffect(() => {
    const fetchGroupData = async () => {
      if (!user) return;

      try {
        setLoading(true);
        setError(null);

        // Super admin: Fetch all groups
        if (isSuperAdminUser) {
          // Fetch groups with member counts using a join query (similar to mobile app)
          let groupsData: any[] | null = null;
          let groupsError: any = null;

          // Try fetching with creator join and member count
          const groupsResult = await supabase
            .from('groups')
            .select(`
              id,
              name,
              description,
              company_name,
              company_logo_url,
              is_active,
              created_at,
              created_by,
              user_profiles!groups_created_by_fkey (
                id,
                full_name,
                email,
                company_logo_url,
                company_name
              ),
              group_members (
                id
              )
            `)
            .order('created_at', { ascending: false });

          if (groupsResult.error) {
            console.warn('Error fetching groups with creator join, trying without join:', groupsResult.error);
            // Fallback: fetch groups without join
            const fallbackResult = await supabase
              .from('groups')
              .select('id, name, description, company_name, company_logo_url, is_active, created_at, created_by')
              .order('created_at', { ascending: false });

            if (fallbackResult.error) {
              console.error('Error fetching groups:', fallbackResult.error);
              throw fallbackResult.error;
            }
            groupsData = fallbackResult.data;
          } else {
            groupsData = groupsResult.data;
          }

          // If we don't have creator info from join, fetch it separately
          let creatorMap: Record<string, { full_name?: string; email?: string; company_logo_url?: string; company_name?: string }> = {};
          
          console.log('🔵 ====== START: Fetching creator profiles ======');
          if (groupsData) {
            const creatorIds = [...new Set(groupsData.map(g => g.created_by).filter(Boolean))];
            console.log('🔵 Creator IDs to fetch:', creatorIds);

            if (creatorIds.length > 0) {
              const { data: creators, error: creatorsError } = await supabase
                .from('user_profiles')
                .select('id, full_name, email, company_logo_url, company_name')
                .in('id', creatorIds);

              if (creatorsError) {
                console.error('Error fetching creators:', creatorsError);
              }

              if (creators) {
                console.log('🔵 Fetched creators:', creators);
                creatorMap = creators.reduce((acc, creator) => {
                  acc[creator.id] = { 
                    full_name: creator.full_name, 
                    email: creator.email,
                    company_logo_url: creator.company_logo_url,
                    company_name: creator.company_name
                  };
                  console.log(`🔵 Creator ${creator.id} (${creator.full_name}): company_logo_url = ${creator.company_logo_url || 'NONE - NO LOGO UPLOADED'}`);
                  return acc;
                }, {} as Record<string, { full_name?: string; email?: string; company_logo_url?: string; company_name?: string }>);
                console.log('🔵 Final creatorMap:', creatorMap);
              } else {
                console.log('🔵 ⚠️ No creators returned from query');
              }
            } else {
              console.log('🔵 ⚠️ No creator IDs to fetch');
            }
          } else {
            console.log('🔵 ⚠️ groupsData is null/undefined');
          }
          console.log('🔵 ====== END: Fetching creator profiles ======');

            // Merge creator info into groups (always update to ensure we have latest data)
            if (groupsData) {
              groupsData = groupsData.map(group => {
                if (group.created_by) {
                  const creator = creatorMap[group.created_by];
                  // Always merge creator data, even if user_profiles exists from join
                  // This ensures we have the latest company_logo_url and company_name
                  if (creator) {
                    group.user_profiles = { 
                      ...(group.user_profiles || {}),
                      full_name: creator.full_name || group.user_profiles?.full_name, 
                      email: creator.email || group.user_profiles?.email,
                      company_logo_url: creator.company_logo_url || group.user_profiles?.company_logo_url,
                      company_name: creator.company_name || group.user_profiles?.company_name
                    };
                  } else if (!group.user_profiles) {
                    group.user_profiles = null;
                  }
                }
                return group;
              });
            }

          // Count members per group - RLS might block bulk queries, so query per group
          // This matches the pattern used in loadGroupDetails which works
          console.log('=== Counting members for each group ===');
          console.log('Total groups to process:', groupsData?.length || 0);

          const memberCounts: Record<string, number> = {};

          // Query each group individually (same pattern as loadGroupDetails which works)
          // RLS policies might allow per-group queries but block bulk queries
          const countPromises = (groupsData || []).map(async (group: any) => {
            try {
              // Use the exact same query that works in loadGroupDetails
              const { data: members, error: membersError } = await supabase
                .from('group_members')
                .select('id, user_id, group_id, role')
                .eq('group_id', group.id);

              if (membersError) {
                console.error(`✗ Error for group ${group.name} (${group.id}):`, {
                  code: membersError.code,
                  message: membersError.message,
                  details: membersError.details,
                  hint: membersError.hint
                });
                return { groupId: group.id, count: 0 };
              }

              const count = members?.length || 0;
              if (count > 0) {
                console.log(`✓ Group ${group.name} (${group.id}): ${count} members found`);
              } else {
                console.warn(`⚠ Group ${group.name} (${group.id}): Query succeeded but returned 0 members (RLS may be filtering)`);
              }
              return { groupId: group.id, count: count };
            } catch (err: any) {
              console.error(`✗ Exception for group ${group.name}:`, err);
              return { groupId: group.id, count: 0 };
            }
          });

          const countResults = await Promise.all(countPromises);
          countResults.forEach(({ groupId, count }) => {
            memberCounts[groupId] = count;
          });

          console.log('Member counts after per-group queries:', memberCounts);

          console.log('=== Final member counts summary ===');
          console.log('Member counts object:', memberCounts);
          console.log('Total groups with members:', Object.values(memberCounts).filter(c => c > 0).length);
          console.log('Total groups with 0 members:', Object.values(memberCounts).filter(c => c === 0).length);

          // Map groups with member counts - prioritize calculated counts
          console.log('🟢 [MAPPING] Starting to map groups. creatorMap keys:', Object.keys(creatorMap));
          console.log('🟢 [MAPPING] creatorMap contents:', creatorMap);
          
          const groupsWithCounts = (groupsData || []).map((group: any) => {
            // Use calculated count first (most reliable)
            let memberCount = memberCounts[group.id] || 0;

            // If calculated count is 0, check join query as fallback
            if (memberCount === 0 && group.group_members) {
              if (Array.isArray(group.group_members)) {
                memberCount = group.group_members.length;
                console.log(`Group ${group.name}: Using ${memberCount} members from join array (calculated was 0)`);
              } else if (typeof group.group_members === 'object' && group.group_members !== null) {
                memberCount = 1;
                console.log(`Group ${group.name}: Using 1 member from join object (calculated was 0)`);
              }
            }

            // Get creator name
            const creator = group.user_profiles;
            const creatorName = creator?.full_name || creator?.email || 'Unknown';

            // Get creator's company logo - check both the merged user_profiles and the creatorMap
            let creatorCompanyLogo = creator?.company_logo_url;
            let creatorCompanyName = creator?.company_name;
            
            console.log(`🟢 [MAPPING] Group "${group.name}" (created_by: ${group.created_by}):`);
            console.log(`  - creator?.company_logo_url: ${creator?.company_logo_url || 'none'}`);
            console.log(`  - creatorMap[${group.created_by}]:`, creatorMap[group.created_by]);
            
            // If not found in merged data, try to get from creatorMap directly
            if (!creatorCompanyLogo && group.created_by) {
              const directCreator = creatorMap[group.created_by];
              console.log(`🟢 [MAPPING] Checking creatorMap for created_by: ${group.created_by}`, directCreator);
              if (directCreator) {
                creatorCompanyLogo = directCreator.company_logo_url;
                creatorCompanyName = directCreator.company_name;
                console.log(`🟢 [MAPPING] ✓ Found in creatorMap - company_logo_url: ${creatorCompanyLogo || 'NONE'}`);
              } else {
                console.log(`🟢 [MAPPING] ✗ creatorMap entry NOT FOUND for created_by: ${group.created_by}`);
                console.log(`🟢 [MAPPING] Available creatorMap keys:`, Object.keys(creatorMap));
              }
            } else if (creatorCompanyLogo) {
              console.log(`🟢 [MAPPING] ✓ Found logo in merged creator data: ${creatorCompanyLogo}`);
            }

            console.log(`Final mapping: Group "${group.name}" (${group.id}): ${memberCount} members, creator: ${creatorName}`);
            console.log(`  - creatorCompanyLogo: ${creatorCompanyLogo || 'none'}`);
            console.log(`  - group.company_logo_url: ${group.company_logo_url || 'none'}`);
            console.log(`  - creator object:`, creator);
            console.log(`  - creatorMap entry:`, creatorMap[group.created_by || '']);
            console.log(`  - creatorMap keys:`, Object.keys(creatorMap));

            // Priority: Use creator's company logo if group doesn't have one, or if group logo is empty/null
            // This ensures the creator's branding is always shown
            const displayLogoUrl = (group.company_logo_url && group.company_logo_url.trim() !== '') 
              ? group.company_logo_url 
              : (creatorCompanyLogo && creatorCompanyLogo.trim() !== '') 
                ? creatorCompanyLogo 
                : '/assets/images/ihslogo.png';
            // Priority: group.company_name > creator.company_name
            const displayCompanyName = (group.company_name && group.company_name.trim() !== '') 
              ? group.company_name 
              : creatorCompanyName;

            return {
              id: group.id,
              name: group.name || 'Unnamed Group',
              description: group.description || '',
              memberCount: memberCount, // Use the calculated/join count
              isActive: group.is_active || false,
              createdAt: group.created_at,
              createdBy: group.created_by,
              creatorName: creatorName,
              companyName: displayCompanyName,
              companyLogoUrl: displayLogoUrl,
              creatorCompanyLogoUrl: (creatorCompanyLogo && creatorCompanyLogo.trim() !== '') ? creatorCompanyLogo : null // Store creator's logo for display priority (use null instead of undefined for better debugging)
            };
          });

          console.log('=== Groups with counts (final) ===');
          console.log('🔍 DEBUG: creatorMap at this point:', creatorMap);
          console.log('🔍 DEBUG: creatorMap keys:', Object.keys(creatorMap));
          groupsWithCounts.forEach(g => {
            console.log(`  - ${g.name}: ${g.memberCount} members`);
            console.log(`    creatorCompanyLogoUrl: ${g.creatorCompanyLogoUrl || 'NONE'}`);
            console.log(`    companyLogoUrl: ${g.companyLogoUrl || 'NONE'}`);
            console.log(`    createdBy: ${g.createdBy || 'NONE'}`);
            if (g.createdBy && creatorMap[g.createdBy]) {
              console.log(`    🔍 Creator in map:`, creatorMap[g.createdBy]);
            } else {
              console.log(`    ⚠️ Creator NOT in map for createdBy: ${g.createdBy}`);
            }
          });
          
          console.log('=== Setting allGroups state ===');
          console.log('Total groups:', groupsWithCounts.length);

          setAllGroups(groupsWithCounts);

          setLoading(false);
          return;
        }

        // Regular admin: Get user's groups using the same approach as mobile app
        // Mobile app uses: groups.select(...).eq('group_members.user_id', userId).eq('is_active', true)
        // First, try to get groups where user is a member (using inner join like mobile)
        let userGroups: any[] | null = null;
        let groupsError: any = null;

        try {
          const result = await supabase
            .from('groups')
            .select(`
            id,
            name,
            description,
            company_name,
            company_logo_url,
            created_by,
            created_at,
            is_active,
            group_members!inner (
              user_id,
              role
            )
          `)
            .eq('group_members.user_id', user.id)
            .eq('is_active', true)
            .order('created_at', { ascending: false });

          userGroups = result.data;
          groupsError = result.error;
        } catch (err) {
          console.warn('Inner join query failed, trying fallback:', err);
          groupsError = err;
        }

        // If inner join fails or returns no results, try fallbacks
        if (groupsError || !userGroups || userGroups.length === 0) {
          console.log('Trying fallback methods to find groups...');

          // Fallback 1: Get groups where user is creator
          const { data: createdGroups, error: createdError } = await supabase
            .from('groups')
            .select('*')
            .eq('created_by', user.id)
            .eq('is_active', true)
            .order('created_at', { ascending: false });

          if (!createdError && createdGroups && createdGroups.length > 0) {
            console.log('Found groups by creator:', createdGroups.length);
            
            // Fetch creator profiles for these groups
            let creatorMap: Record<string, { full_name?: string; email?: string; company_logo_url?: string; company_name?: string }> = {};
            const creatorIds = [...new Set(createdGroups.map((g: any) => g.created_by).filter(Boolean))];
            if (creatorIds.length > 0) {
              const { data: creators } = await supabase
                .from('user_profiles')
                .select('id, full_name, email, company_logo_url, company_name')
                .in('id', creatorIds);
              if (creators) {
                creatorMap = creators.reduce((acc, creator) => {
                  acc[creator.id] = { 
                    full_name: creator.full_name, 
                    email: creator.email,
                    company_logo_url: creator.company_logo_url,
                    company_name: creator.company_name
                  };
                  return acc;
                }, {} as Record<string, { full_name?: string; email?: string; company_logo_url?: string; company_name?: string }>);
              }
            }
            
            setAllGroups(
              createdGroups.map((g: any) => {
                const creator = creatorMap[g.created_by || ''];
                const creatorCompanyLogo = creator?.company_logo_url;
                const creatorCompanyName = creator?.company_name;
                const displayLogoUrl = (g.company_logo_url && g.company_logo_url.trim() !== '') 
                  ? g.company_logo_url 
                  : (creatorCompanyLogo && creatorCompanyLogo.trim() !== '') 
                    ? creatorCompanyLogo 
                    : '/assets/images/ihslogo.png';
                const displayCompanyName = (g.company_name && g.company_name.trim() !== '') 
                  ? g.company_name 
                  : creatorCompanyName;
                
                return {
                  id: g.id,
                  name: g.name || 'Unnamed Group',
                  description: g.description || '',
                  memberCount: 0,
                  isActive: Boolean(g.is_active),
                  createdAt: g.created_at,
                  createdBy: g.created_by,
                  companyName: displayCompanyName,
                  companyLogoUrl: displayLogoUrl,
                  creatorCompanyLogoUrl: (creatorCompanyLogo && creatorCompanyLogo.trim() !== '') ? creatorCompanyLogo : null,
                  creatorName: creator?.full_name || creator?.email || 'Unknown',
                };
              })
            );

            // Pick the previously selected group if it still exists, otherwise default to most recent
            const createdIds = createdGroups.map((g: any) => g.id);
            const nextGroupId =
              selectedGroupId && createdIds.includes(selectedGroupId)
                ? selectedGroupId
                : createdIds.length === 1
                  ? createdIds[0]
                  : null;

            setSelectedGroupId(nextGroupId);
            setLoading(false);
            return;
          }

          // Fallback 2: Get groups from group_members table directly
          const { data: memberGroups, error: memberError } = await supabase
            .from('group_members')
            .select('group_id, groups(*)')
            .eq('user_id', user.id);

          if (!memberError && memberGroups && memberGroups.length > 0) {
            console.log('Found groups from group_members:', memberGroups.length);
            
            // Extract groups and fetch creator profiles
            const extractedGroups = (memberGroups || [])
              .map((row: any) => {
                const g = Array.isArray(row.groups) ? row.groups[0] : row.groups;
                return g;
              })
              .filter(Boolean);
            
            // Fetch creator profiles
            let creatorMap: Record<string, { full_name?: string; email?: string; company_logo_url?: string; company_name?: string }> = {};
            const creatorIds = [...new Set(extractedGroups.map((g: any) => g.created_by).filter(Boolean))];
            if (creatorIds.length > 0) {
              const { data: creators } = await supabase
                .from('user_profiles')
                .select('id, full_name, email, company_logo_url, company_name')
                .in('id', creatorIds);
              if (creators) {
                creatorMap = creators.reduce((acc, creator) => {
                  acc[creator.id] = { 
                    full_name: creator.full_name, 
                    email: creator.email,
                    company_logo_url: creator.company_logo_url,
                    company_name: creator.company_name
                  };
                  return acc;
                }, {} as Record<string, { full_name?: string; email?: string; company_logo_url?: string; company_name?: string }>);
              }
            }
            
            const groupsFromMembership = (memberGroups || [])
              .map((row: any) => {
                const g = Array.isArray(row.groups) ? row.groups[0] : row.groups;
                if (!g) return null;
                
                const creator = creatorMap[g.created_by || ''];
                const creatorCompanyLogo = creator?.company_logo_url;
                const creatorCompanyName = creator?.company_name;
                const displayLogoUrl = (g.company_logo_url && g.company_logo_url.trim() !== '') 
                  ? g.company_logo_url 
                  : (creatorCompanyLogo && creatorCompanyLogo.trim() !== '') 
                    ? creatorCompanyLogo 
                    : '/assets/images/ihslogo.png';
                const displayCompanyName = (g.company_name && g.company_name.trim() !== '') 
                  ? g.company_name 
                  : creatorCompanyName;
                
                return {
                  id: row.group_id,
                  name: g.name || 'Unnamed Group',
                  description: g.description || '',
                  memberCount: 0,
                  isActive: Boolean(g.is_active),
                  createdAt: g.created_at,
                  createdBy: g.created_by,
                  companyName: displayCompanyName,
                  companyLogoUrl: displayLogoUrl,
                  creatorCompanyLogoUrl: (creatorCompanyLogo && creatorCompanyLogo.trim() !== '') ? creatorCompanyLogo : null,
                  creatorName: creator?.full_name || creator?.email || 'Unknown',
                };
              })
              .filter(Boolean) as Array<{
              id: string;
              name: string;
              description: string;
              memberCount: number;
              isActive: boolean;
              createdAt: string;
              createdBy?: string;
              companyName?: string;
              companyLogoUrl?: string;
              creatorCompanyLogoUrl?: string | null;
              creatorName?: string;
            }>;

            if (groupsFromMembership.length > 0) {
              setAllGroups(groupsFromMembership);
              const ids = groupsFromMembership.map(g => g.id);
              const nextGroupId =
                selectedGroupId && ids.includes(selectedGroupId)
                  ? selectedGroupId
                  : ids.length === 1
                    ? ids[0]
                    : null;
              setSelectedGroupId(nextGroupId);
              setLoading(false);
              return;
            }
          }

          // If still no groups found, show error
          console.error('No groups found with any method');
          setError('No group found. Please create a group first or ensure you are added as a member.');
          setLoading(false);
          return;
        }

        // Fetch creator profiles for all groups (same logic as super admin)
        let creatorMap: Record<string, { full_name?: string; email?: string; company_logo_url?: string; company_name?: string }> = {};
        
        console.log('🔵 [NON-SUPER-ADMIN] ====== START: Fetching creator profiles ======');
        if (userGroups && userGroups.length > 0) {
          const creatorIds = [...new Set(userGroups.map(g => g.created_by).filter(Boolean))];
          console.log('🔵 [NON-SUPER-ADMIN] Creator IDs to fetch:', creatorIds);

          if (creatorIds.length > 0) {
            const { data: creators, error: creatorsError } = await supabase
              .from('user_profiles')
              .select('id, full_name, email, company_logo_url, company_name')
              .in('id', creatorIds);

            if (creatorsError) {
              console.error('🔵 [NON-SUPER-ADMIN] Error fetching creators:', creatorsError);
            }

            if (creators) {
              console.log('🔵 [NON-SUPER-ADMIN] Fetched creators:', creators);
              creatorMap = creators.reduce((acc, creator) => {
                acc[creator.id] = { 
                  full_name: creator.full_name, 
                  email: creator.email,
                  company_logo_url: creator.company_logo_url,
                  company_name: creator.company_name
                };
                console.log(`🔵 [NON-SUPER-ADMIN] Creator ${creator.id} (${creator.full_name}): company_logo_url = ${creator.company_logo_url || 'NONE - NO LOGO UPLOADED'}`);
                return acc;
              }, {} as Record<string, { full_name?: string; email?: string; company_logo_url?: string; company_name?: string }>);
              console.log('🔵 [NON-SUPER-ADMIN] Final creatorMap:', creatorMap);
            } else {
              console.log('🔵 [NON-SUPER-ADMIN] ⚠️ No creators returned from query');
            }
          } else {
            console.log('🔵 [NON-SUPER-ADMIN] ⚠️ No creator IDs to fetch');
          }
        } else {
          console.log('🔵 [NON-SUPER-ADMIN] ⚠️ userGroups is null/empty');
        }
        console.log('🔵 [NON-SUPER-ADMIN] ====== END: Fetching creator profiles ======');

        // We have one or more groups - keep a list and allow switching between them
        const groupsList = (userGroups || []).map((g: any) => {
          // Get creator's company logo from creatorMap
          const creator = creatorMap[g.created_by || ''];
          const creatorCompanyLogo = creator?.company_logo_url;
          const creatorCompanyName = creator?.company_name;
          
          console.log(`🟢 [NON-SUPER-ADMIN] Group "${g.name}" (created_by: ${g.created_by}):`);
          console.log(`  - creatorMap entry:`, creator);
          console.log(`  - creatorCompanyLogo: ${creatorCompanyLogo || 'none'}`);
          
          // Priority: group.company_logo_url > creator.company_logo_url > default
          const displayLogoUrl = (g.company_logo_url && g.company_logo_url.trim() !== '') 
            ? g.company_logo_url 
            : (creatorCompanyLogo && creatorCompanyLogo.trim() !== '') 
              ? creatorCompanyLogo 
              : '/assets/images/ihslogo.png';
          // Priority: group.company_name > creator.company_name
          const displayCompanyName = (g.company_name && g.company_name.trim() !== '') 
            ? g.company_name 
            : creatorCompanyName;

          return {
            id: g.id,
            name: g.name || 'Unnamed Group',
            description: g.description || '',
            memberCount: 0,
            isActive: Boolean(g.is_active),
            createdAt: g.created_at,
            createdBy: g.created_by,
            companyName: displayCompanyName,
            companyLogoUrl: displayLogoUrl,
            creatorCompanyLogoUrl: (creatorCompanyLogo && creatorCompanyLogo.trim() !== '') ? creatorCompanyLogo : null,
            creatorName: creator?.full_name || creator?.email || 'Unknown',
          };
        });

        setAllGroups(groupsList);

        const ids = groupsList.map(g => g.id);
        const nextGroupId =
          selectedGroupId && ids.includes(selectedGroupId)
            ? selectedGroupId
            : ids.length === 1
              ? ids[0]
              : null;

        setSelectedGroupId(nextGroupId);

      } catch (err: any) {
        console.error('Error fetching group data:', err);
        setError(err.message || 'Failed to load group data');
      } finally {
        setLoading(false);
      }
    };

    fetchGroupData();
  }, [user, userProfile, isSuperAdminUser]);


  // When selected group changes, load details and subscribe to realtime updates
  useEffect(() => {
    if (!selectedGroupId) return;

    let cancelled = false;
    (async () => {
      try {
        await loadGroupDetails(selectedGroupId);
        if (!cancelled) {
          setupRealtimeSync(selectedGroupId);
        }
      } catch (e) {
        // loadGroupDetails already sets error state/logs; keep this as a safety net
        console.error('Failed to load selected group details:', e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedGroupId]);

  const showGroupListForAdmin = !loading && !isSuperAdminUser && allGroups.length > 1 && !selectedGroupId;

  // Function to load group details
  const loadGroupDetails = async (groupId: string) => {
    try {
      setCurrentGroupId(groupId);
      setLoading(true);

      const normalizeTime = (t: any, fallback: string) => {
        if (!t) return fallback;
        const s = String(t);
        // Postgres TIME often comes back as "HH:MM:SS"
        return s.length >= 5 ? s.slice(0, 5) : fallback;
      };

      // Fetch group data
      const { data: groupData, error: groupError } = await supabase
        .from('groups')
        .select('*')
        .eq('id', groupId)
        .single();

      if (groupError) throw groupError;

      // Set group settings
      setGroupSettings({
        id: groupData.id,
        name: groupData.name || '',
        description: groupData.description || '',
        createdAt: new Date(groupData.created_at),
        memberCount: 0, // Will be updated below
        totalContributions: 0, // Will be updated below
        membershipFee: groupData.membership_fee || 0,
        companyName: groupData.company_name || undefined,
        companyLogoUrl: groupData.company_logo_url || undefined,
        createdBy: groupData.created_by || undefined
      });

      // Set contribution schedule from group settings
      setContributionSchedule({
        id: groupData.id,
        contributionsEnabled: groupData.contributions_enabled !== false,
        lendingEnabled: groupData.lending_enabled !== false,
        // New contribution schedule (days of week, time, timezone)
        days: Array.isArray(groupData.contribution_days) ? groupData.contribution_days : [],
        time: normalizeTime(groupData.contribution_time, '09:00'),
        timezone: groupData.contribution_timezone || 'Pacific/Port_Vila',
        reminderEnabled: Boolean(groupData.contribution_reminder_enabled),
        reminderTime: normalizeTime(groupData.contribution_reminder_time, '08:00'),
        // Mobile schedule (collection/lending day, frequency, amount)
        collectionDay: groupData.collection_day || null,
        lendingDay: groupData.lending_day || null,
        contributionFrequency: groupData.contribution_frequency || 'monthly',
        contributionAmount: Number(groupData.contribution_amount) || 0,
        currency: groupData.currency || 'VT',
        membershipFee: Number(groupData.membership_fee) || 0,
      });

      // Set contribution amount limits
      setContributionSettings({
        minContributionAmount: groupData.min_contribution_amount || null,
        maxContributionAmount: groupData.max_contribution_amount || null,
      });

      // Fetch group members with fee status using RPC
      const { data: membersWithFees, error: membersError } = await supabase.rpc(
        'get_members_with_fee_status',
        { p_group_id: groupId }
      );

      if (membersError) throw membersError;

      // Fetch contributions for each member to calculate totals
      const memberIds = membersWithFees?.map((gm: any) => gm.user_id) || [];
      let contributionsMap: Record<string, { total: number; lastDate: Date | null }> = {};

      if (memberIds.length > 0) {
        // Contributions table uses 'member_id', not 'user_id'
        const { data: contributions, error: contribError } = await supabase
          .from('contributions')
          .select('member_id, amount, contribution_date')
          .eq('group_id', groupId)
          .eq('status', 'approved')
          .in('member_id', memberIds);

        if (contribError) {
          console.error('Error fetching contributions:', contribError);
        }

        if (!contribError && contributions) {
          console.log(`Found ${contributions.length} contributions for group ${groupId}`);
          contributions.forEach((contrib: any) => {
            const memberId = contrib.member_id;
            if (!contributionsMap[memberId]) {
              contributionsMap[memberId] = { total: 0, lastDate: null };
            }
            contributionsMap[memberId].total += Number(contrib.amount || 0);
            const contribDate = new Date(contrib.contribution_date);
            if (!contributionsMap[memberId].lastDate || contribDate > contributionsMap[memberId].lastDate!) {
              contributionsMap[memberId].lastDate = contribDate;
            }
          });
          console.log('Contributions map:', contributionsMap);
        } else if (!contribError && (!contributions || contributions.length === 0)) {
          console.warn(`No contributions found for group ${groupId} (query succeeded but returned empty)`);
        }
      }

      // Calculate total contributions
      const totalContributions = Object.values(contributionsMap).reduce(
        (sum, c) => sum + c.total, 0
      );

      // Cash in hand (per-group) for admin users
      let cashInHand: number | undefined;
      if (canSeeCashInHand) {
        try {
          cashInHand = await loanService.getGroupCashInHand(groupId);
        } catch (e) {
          console.warn('Failed to load cash in hand:', e);
        }
      }

      // Map members to GroupMember format
      const mappedMembers: GroupMember[] = (membersWithFees || []).map((gm: any) => {
        const contribData = contributionsMap[gm.user_id] || { total: 0, lastDate: null };

        return {
          id: gm.user_id,
          name: gm.full_name || 'Unknown',
          email: gm.email || '',
          role: (gm.role === 'admin' || gm.role === 'group_admin') ? 'admin' : gm.role === 'clerk' ? 'clerk' : 'member',
          joinDate: new Date(gm.joined_at),
          avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(gm.full_name || 'Member')}`,
          alt: `${gm.full_name || 'User'} profile picture`,
          totalContributions: contribData.total,
          lastContribution: contribData.lastDate,
          status: 'active',
          membershipFeePaid: gm.membership_fee_paid,
          membershipFeeStatus: gm.payment_status as any
        };
      });

      setMembers(mappedMembers);
      console.log('=== Member Management Debug ===');
      console.log('Group ID:', groupId);
      console.log('Members loaded:', mappedMembers.length);
      console.log('Members data:', mappedMembers);
      setGroupSettings(prev => ({
        ...prev,
        memberCount: mappedMembers.length,
        totalContributions: totalContributions,
        ...(cashInHand !== undefined && { moneyInHand: cashInHand }),
      }));

      // Set up real-time sync for this group
      setupRealtimeSync(groupId);
    } catch (err: any) {
      console.error('Error loading group details:', err);
      setError(err.message || 'Failed to load group details');
    } finally {
      setLoading(false);
    }
  };

  // Set up real-time sync for group changes
  const setupRealtimeSync = (groupId: string) => {
    // Unsubscribe from previous group if any
    realtimeSyncService.unsubscribe();

    // Set up callbacks for real-time updates
    realtimeSyncService.onMemberAdded = async (newMember: any) => {
      console.log('🔄 Real-time: Member added, refreshing list...');
      // Reload members to get full profile data
      await loadGroupDetails(groupId);
    };

    realtimeSyncService.onMemberUpdated = async (updatedMember: any) => {
      console.log('🔄 Real-time: Member updated, refreshing list...');
      // Reload members to get updated data
      await loadGroupDetails(groupId);
    };

    realtimeSyncService.onMemberDeleted = async (deletedMemberId: string) => {
      console.log('🔄 Real-time: Member deleted, refreshing list...');
      // Remove from local state immediately
      setMembers(prev => prev.filter(m => m.id !== deletedMemberId));
      setGroupSettings(prev => ({
        ...prev,
        memberCount: Math.max(0, prev.memberCount - 1)
      }));
      // Reload to ensure consistency
      await loadGroupDetails(groupId);
    };

    realtimeSyncService.onGroupUpdated = async (updatedGroup: any) => {
      console.log('🔄 Real-time: Group updated, refreshing...');
      await loadGroupDetails(groupId);
    };

    realtimeSyncService.onContributionAdded = async () => {
      console.log('🔄 Real-time: Contribution added, refreshing...');
      await loadGroupDetails(groupId);
    };

    realtimeSyncService.onContributionUpdated = async () => {
      console.log('🔄 Real-time: Contribution updated, refreshing...');
      await loadGroupDetails(groupId);
    };

    realtimeSyncService.onContributionDeleted = async () => {
      console.log('🔄 Real-time: Contribution deleted, refreshing...');
      await loadGroupDetails(groupId);
    };

    // Subscribe to real-time updates
    realtimeSyncService.subscribeToGroup(groupId).catch(err => {
      console.error('Failed to subscribe to real-time updates:', err);
    });
  };

  // Cleanup real-time sync on unmount
  useEffect(() => {
    return () => {
      realtimeSyncService.unsubscribe();
    };
  }, []);

  const refreshData = () => {
    // Trigger re-fetch by updating a dependency
    if (user) {
      const fetchGroupData = async () => {
        // Same logic as useEffect above - could be extracted to a function
        window.location.reload(); // Simple refresh for now
      };
      fetchGroupData();
    }
  };

  const handleUpdateGroupSettings = async (updates: Partial<GroupSettings>) => {
    if (!currentGroupId) return;

    try {
      const updateData: any = {};
      if (updates.name) updateData.name = updates.name;
      if (updates.description !== undefined) updateData.description = updates.description;
      if (updates.membershipFee !== undefined) updateData.membership_fee = updates.membershipFee;
      if (updates.companyName !== undefined) updateData.company_name = updates.companyName || null;
      if (updates.companyLogoUrl !== undefined) updateData.company_logo_url = updates.companyLogoUrl || null;

      const { error } = await supabase
        .from('groups')
        .update(updateData)
        .eq('id', currentGroupId);

      if (error) throw error;
      setGroupSettings({ ...groupSettings, ...updates });
    } catch (err: any) {
      console.error('Error updating group settings:', err);
      setError(err.message || 'Failed to update group settings');
    }
  };

  const handleUpdateContributionSettings = async (settings: {
    minContributionAmount: number | null;
    maxContributionAmount: number | null;
  }) => {
    if (!currentGroupId) return;

    try {
      const { error } = await supabase
        .from('groups')
        .update({
          min_contribution_amount: settings.minContributionAmount,
          max_contribution_amount: settings.maxContributionAmount,
        })
        .eq('id', currentGroupId);

      if (error) throw error;

      setContributionSettings(settings);
    } catch (err: any) {
      console.error('Error updating contribution settings:', err);
      setError(err.message || 'Failed to update contribution settings');
    }
  };

  const handleUpdateSchedule = async (updates: Partial<ContributionSchedule>) => {
    if (!currentGroupId) return;

    try {
      const updateData: any = {};
      // New contribution schedule fields
      if (typeof updates.timezone === 'string') updateData.contribution_timezone = updates.timezone;
      if (Array.isArray(updates.days)) updateData.contribution_days = updates.days;
      if (typeof updates.time === 'string') updateData.contribution_time = updates.time;
      if (typeof updates.reminderEnabled === 'boolean') updateData.contribution_reminder_enabled = updates.reminderEnabled;
      if (typeof updates.reminderTime === 'string') updateData.contribution_reminder_time = updates.reminderTime;
      // Mobile schedule fields
      if (updates.collectionDay !== undefined) updateData.collection_day = updates.collectionDay;
      if (updates.lendingDay !== undefined) updateData.lending_day = updates.lendingDay;
      if (typeof updates.contributionFrequency === 'string') updateData.contribution_frequency = updates.contributionFrequency;
      if (typeof updates.contributionAmount === 'number') updateData.contribution_amount = updates.contributionAmount;
      if (typeof updates.currency === 'string') updateData.currency = updates.currency;
      if (typeof updates.membershipFee === 'number') updateData.membership_fee = updates.membershipFee;
      if (typeof updates.contributionsEnabled === 'boolean') updateData.contributions_enabled = updates.contributionsEnabled;
      if (typeof updates.lendingEnabled === 'boolean') updateData.lending_enabled = updates.lendingEnabled;

      if (Object.keys(updateData).length === 0) {
        // Nothing to persist; still update local state to keep UI consistent
        setContributionSchedule({ ...contributionSchedule, ...updates });
        return;
      }

      const { error } = await supabase
        .from('groups')
        .update(updateData)
        .eq('id', currentGroupId);

      if (error) throw error;
      setContributionSchedule({ ...contributionSchedule, ...updates });
    } catch (err: any) {
      console.error('Error updating schedule:', err);
      setError(err.message || 'Failed to update schedule');
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!currentGroupId) return;

    try {
      const { error } = await supabase
        .from('group_members')
        .delete()
        .eq('group_id', currentGroupId)
        .eq('user_id', memberId);

      if (error) throw error;

      setMembers(members.filter((m) => m.id !== memberId));
      setGroupSettings({
        ...groupSettings,
        memberCount: groupSettings.memberCount - 1
      });
    } catch (err: any) {
      console.error('Error removing member:', err);
      setError(err.message || 'Failed to remove member');
    }
  };

  const handleChangeRole = async (memberId: string, newRole: 'admin' | 'member' | 'clerk') => {
    if (!currentGroupId) return;

    // Super_admin can change all roles (including group_creator)
    // Prevent role changes only for group_admin
    // Group Creator and assigned admins (group_members.role='admin') can change roles
    if (userProfile?.role === 'group_admin') {
      setError('Group Admin cannot change member roles. Super Admin, Group Creator, and assigned group admins can change roles.');
      return;
    }

    // Group creator cannot change their own role - they are the owner of the group
    // Exception: super_admin can change group_creator's role
    if (userProfile?.role === 'group_creator' && memberId === userProfile?.id && userProfile?.role !== 'super_admin') {
      setError('You cannot change your own role. As the group creator, you are the owner and must remain an admin.');
      return;
    }

    try {
      const { error } = await supabase
        .from('group_members')
        .update({ role: newRole })
        .eq('group_id', currentGroupId)
        .eq('user_id', memberId);

      if (error) throw error;

      setMembers(
        members.map((m) => m.id === memberId ? { ...m, role: newRole } : m)
      );
    } catch (err: any) {
      console.error('Error changing role:', err);
      setError(err.message || 'Failed to change member role');
    }
  };

  const handlePayMembershipFee = async (memberId: string, amount: number) => {
    if (!currentGroupId || !user) return;

    try {
      // Find the member to get their current paid amount
      const member = members.find(m => m.id === memberId);
      const previousPaid = member?.membershipFeePaid || 0;
      const newTotal = previousPaid + amount;

      const { error } = await supabase
        .from('membership_fee_payments')
        .upsert({
          group_id: currentGroupId,
          member_id: memberId,
          amount: newTotal,
          recorded_by: user.id,
          paid_at: new Date().toISOString()
        }, { onConflict: 'group_id, member_id' });

      if (error) throw error;

      // Refresh members
      await loadGroupDetails(currentGroupId);
      alert('Membership fee payment recorded successfully.');
    } catch (err: any) {
      console.error('Error recording membership fee:', err);
      alert(err.message || 'Failed to record membership fee');
    }
  };

  const handleRegisterMember = async (
    nationalId: string,
    password: string,
    fullName: string,
    role: 'admin' | 'member' | 'clerk'
    ,
    phone?: string,
    idPhotoFile?: File,
    isBulkImport: boolean = false
  ) => {
    if (!currentGroupId || !user) {
      setError('You must be logged in to register members.');
      return;
    }

    setIsRegisteringMember(true);
    setError(null);

    try {
      // Verify user is authenticated - use the user from context
      if (!user || !user.id) {
        throw new Error('You must be logged in to register members. Please refresh the page or log out and log back in.');
      }

      // Check subscription limits and payment status
      const canAdd = await canAddGroupMember(currentGroupId);

      if (!canAdd.allowed) {
        // Create error with upgrade link if needed
        const error = new Error(canAdd.reason || 'Cannot add member. Please check your subscription limits or payment status.');
        (error as any).needsUpgrade = canAdd.needs_upgrade === true;
        throw error;
      }

      const cleanedNationalId = nationalId.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
      if (!cleanedNationalId) {
        throw new Error('National ID is required');
      }

      // Convert national ID to auth email format (stable mapping for Supabase Auth)
      const authEmail = `nid_${cleanedNationalId.toLowerCase()}@nid.local`;

      // Check if user already exists by checking user_profiles (national_id is login identifier)
      const { data: existingProfile } = await supabase
        .from('user_profiles')
        .select('id, full_name, national_id, email, phone, national_id_photo_url')
        .or(`national_id.eq.${cleanedNationalId},email.eq.${authEmail}`)
        .maybeSingle();

      // If the user already exists, we simply add/update group membership (no password change).
      // If the user does NOT exist, we create an auth account (requires password).
      const isExistingUser = Boolean(existingProfile?.id);

      if (!isExistingUser) {
        // Allow creating clerks and admins during bulk import
        // For manual registration, clerks must already exist (security best practice)
        // But during CSV import, we allow creating them
        if (role === 'clerk' && !isBulkImport) {
          throw new Error('Clerk must already have an account. Please ask the user to register as a member first, or use CSV import to create them.');
        }
        if (!password) {
          throw new Error('Password is required');
        }
        // For bulk import, allow creating admins and clerks without ID photo requirement
        // Members still need ID photo unless it's bulk import
        if (role === 'member' && !idPhotoFile && !isBulkImport) {
          throw new Error('National ID card photo is required for members');
        }
      } else {
        // Existing member: only require ID photo if it is missing on file and the role is member.
        if (role === 'member' && !existingProfile?.national_id_photo_url && !idPhotoFile && !isBulkImport) {
          throw new Error('National ID card photo is required for members');
        }
      }

      let memberUserId: string;
      let memberFullName: string = fullName?.trim() || '';
      let createdNewUser = false;

      if (isExistingUser && existingProfile?.id) {
        memberUserId = existingProfile.id;
        memberFullName = existingProfile.full_name || memberFullName || 'Member';
      } else {
        // Store the current admin's session before creating new user
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (!currentSession || !currentSession.user) {
          throw new Error('Your session has expired. Please log out and log back in.');
        }

        const adminUserId = currentSession.user.id;
        const adminAccessToken = currentSession.access_token;
        const adminRefreshToken = currentSession.refresh_token;

        // Create a separate Supabase client instance to avoid triggering auth state changes
        // in the main client's auth context
        const { createClient } = await import('@supabase/supabase-js');
        const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
        const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

        if (!supabaseUrl || !supabaseAnonKey) {
          throw new Error('Missing Supabase configuration. Please check your environment variables.');
        }

        const tempSupabase = createClient(supabaseUrl, supabaseAnonKey, {
          auth: {
            autoRefreshToken: false,
            persistSession: false,
          }
        });

        // Create new user account using the temporary client
        // This prevents the main auth context from reacting to the sign-up
        const { data: authData, error: signUpError } = await tempSupabase.auth.signUp({
          email: authEmail,
          password: password,
          options: {
            data: {
              full_name: fullName,
              phone: phone,
              national_id: cleanedNationalId,
              role: 'member',
            }
          }
        });

        if (signUpError) {
          // Check if error is because user already exists
          if (signUpError.message.includes('already registered') || signUpError.message.includes('already exists')) {
            throw new Error('A user with this National ID already exists');
          }
          throw signUpError;
        }

        if (!authData.user) {
          throw new Error('Failed to create user account. Please try again.');
        }

        createdNewUser = true;
        memberUserId = authData.user.id;
        memberFullName = fullName?.trim() || 'Member';

        // Upload National ID photo for members using the NEW MEMBER session.
        // This avoids Storage RLS failures (policies often require auth.uid() to match the folder/user).
        if (role === 'member' && idPhotoFile) {
          // Ensure tempSupabase has a session for the newly created user
          if (!authData.session) {
            // Depending on Supabase auth settings (email confirmation), signUp may not return a session.
            // In that case, the admin cannot upload on behalf of the user without a service role / different RLS policy.
            throw new Error(
              'Member created, but ID photo upload requires the member to have an active session. ' +
                'Please have the member log in once and upload their ID photo from their profile, ' +
                'or adjust Storage RLS to allow admins to upload on behalf of members.',
            );
          }

          // IMPORTANT: supabase-js does not always apply the returned session to the client automatically.
          // Explicitly set the member session so Storage requests include the member JWT (auth.uid()).
          const { error: setSessionError } = await tempSupabase.auth.setSession({
            access_token: authData.session.access_token,
            refresh_token: authData.session.refresh_token,
          });
          if (setSessionError) {
            throw new Error(`Member created, but failed to initialize member session for ID upload: ${setSessionError.message}`);
          }

          const safeExt = idPhotoFile.name.split('.').pop() || 'jpg';
          const filePath = `${authData.user.id}/${Date.now()}-national-id.${safeExt}`;

          const { error: uploadError } = await tempSupabase.storage
            .from('id-verifications')
            .upload(filePath, idPhotoFile, {
              contentType: idPhotoFile.type || 'image/jpeg',
              upsert: true,
            });

          if (uploadError) {
            // Fallback: if storage RLS blocks member-session upload for any reason, retry as the current admin.
            // This requires the additional storage policy added in:
            //   supabase/migrations/20260104000001_allow_admin_upload_member_id_photos.sql
            const { error: adminUploadError } = await supabase.storage
              .from('id-verifications')
              .upload(filePath, idPhotoFile, {
                contentType: idPhotoFile.type || 'image/jpeg',
                upsert: true,
              });

            if (adminUploadError) {
              throw new Error(
                `Member created, but ID photo upload failed: ${uploadError.message}. ` +
                  `Admin retry also failed: ${adminUploadError.message}. ` +
                  `Apply the storage policy migration to allow admin uploads.`,
              );
            }
          }

          const { data: { publicUrl } } = supabase.storage
            .from('id-verifications')
            .getPublicUrl(filePath);

          // Save URL on the member's profile
          const { error: profileUpdateError } = await supabase
            .from('user_profiles')
            .update({
              national_id: cleanedNationalId,
              national_id_photo_url: publicUrl,
            })
            .eq('id', authData.user.id);

          if (profileUpdateError) {
            throw new Error(`Member created, but saving ID photo URL failed: ${profileUpdateError.message}`);
          }
        }

        // Sign out the temporary client (this won't affect the main client)
        await tempSupabase.auth.signOut();

        // Verify we're still the admin user in the main client
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        if (currentUser?.id !== adminUserId) {
          // If somehow we lost the session, restore it
          const { error: restoreError } = await supabase.auth.setSession({
            access_token: adminAccessToken,
            refresh_token: adminRefreshToken,
          });

          if (restoreError) {
            console.error('Error restoring admin session:', restoreError);
            throw new Error('Member created successfully, but your session was lost. Please refresh the page.');
          }
        }

        // Wait a moment for the trigger to create the user profile
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Verify user profile was created
        let profile = null;
        let retries = 3;
        while (retries > 0) {
          const { data: profileData, error: profileError } = await supabase
            .from('user_profiles')
            .select('id')
            .eq('id', authData.user.id)
            .single();

          if (profileData && !profileError) {
            profile = profileData;
            break;
          }

          retries--;
          await new Promise(resolve => setTimeout(resolve, 1000));
        }

        if (!profile) {
          // If profile doesn't exist, create it manually
          const { error: createProfileError } = await supabase
            .from('user_profiles')
            .insert({
              id: authData.user.id,
              email: authEmail,
              full_name: fullName,
              phone: phone || null,
              role: 'member',
              national_id: cleanedNationalId,
            });

          if (createProfileError) {
            console.error('Error creating user profile:', createProfileError);
            throw new Error('User account created but profile creation failed. Please contact support.');
          }
        }
      }

      // Verify we're the group creator before attempting insert
      const { data: groupData, error: groupError } = await supabase
        .from('groups')
        .select('id, created_by, name')
        .eq('id', currentGroupId)
        .single();

      if (groupError || !groupData) {
        throw new Error('Failed to verify group ownership. Please try again.');
      }

      if (groupData.created_by !== user?.id) {
        // Allow platform group admins OR assigned group admins.
        if (!(userProfile?.role === 'group_admin' || userProfile?.role === 'super_admin')) {
          const { data: memberCheck } = await supabase
            .from('group_members')
            .select('role')
            .eq('group_id', currentGroupId)
            .eq('user_id', user?.id)
            .eq('role', 'admin')
            .maybeSingle();

          if (!memberCheck) {
            throw new Error(
              `You are not authorized to add members to "${groupData.name}". Only the group creator or an assigned admin can add members.`
            );
          }
        }
      }

      // Check if user is already a member of another group
      const { data: existingMembership, error: membershipCheckError } = await supabase
        .from('group_members')
        .select('group_id, groups!inner(name)')
        .eq('user_id', memberUserId)
        .neq('group_id', currentGroupId)
        .maybeSingle();

      if (membershipCheckError && membershipCheckError.code !== 'PGRST116') {
        console.error('Error checking existing membership:', membershipCheckError);
        throw new Error(`Failed to verify membership: ${membershipCheckError.message}`);
      }

      if (existingMembership) {
        const groupName = existingMembership.groups?.name || 'another group';
        throw new Error(`This user is already a member of "${groupName}". A user can only belong to one group at a time.`);
      }

      // Check if user is already a member of this group
      const { data: existingInThisGroup } = await supabase
        .from('group_members')
        .select('id')
        .eq('group_id', currentGroupId)
        .eq('user_id', memberUserId)
        .maybeSingle();

      if (existingInThisGroup) {
        // If already in the group, update the role and treat it as success.
        const { error: updateRoleError } = await supabase
          .from('group_members')
          .update({ role })
          .eq('group_id', currentGroupId)
          .eq('user_id', memberUserId);

        if (updateRoleError) {
          throw new Error(`This user is already a member of this group, but role update failed: ${updateRoleError.message}`);
        }

        if (currentGroupId) {
          await loadGroupDetails(currentGroupId);
        }

        alert(`Member ${memberFullName || 'User'} role has been updated to ${role}.`);
        return;
      }

      // Add user to the group with selected role
      const { error: addMemberError } = await supabase
        .from('group_members')
        .insert({
          group_id: currentGroupId,
          user_id: memberUserId,
          role: role,
          joined_at: new Date().toISOString(),
        });

      if (addMemberError) {
        console.error('Error adding member to group:', addMemberError);
        console.error('Current user ID:', user?.id);
        console.error('Group creator ID:', groupData.created_by);
        console.error('Group ID:', currentGroupId);

        // Provide more detailed error message
        if (addMemberError.code === '42501' || addMemberError.message.includes('row-level security')) {
          throw new Error(`Permission denied: ${addMemberError.message}. Current user: ${user?.id}, Group creator: ${groupData.created_by}. Please ensure you are the group creator or an admin member.`);
        } else if (addMemberError.code === '23505') {
          throw new Error('This user is already a member of the group.');
        } else {
          throw new Error(`Failed to add member to group: ${addMemberError.message}`);
        }
      }

      // If adding an EXISTING user as a member and we received an ID photo file, upload it after membership exists
      // so Storage RLS can validate group relationship.
      if (isExistingUser && role === 'member' && idPhotoFile) {
        try {
          const safeExt = idPhotoFile.name.split('.').pop() || 'jpg';
          const filePath = `${memberUserId}/${Date.now()}-national-id.${safeExt}`;

          const { error: uploadError } = await supabase.storage
            .from('id-verifications')
            .upload(filePath, idPhotoFile, {
              contentType: idPhotoFile.type || 'image/jpeg',
              upsert: true,
            });

          if (uploadError) {
            throw uploadError;
          }

          const { data: { publicUrl } } = supabase.storage
            .from('id-verifications')
            .getPublicUrl(filePath);

          const { error: profileUpdateError } = await supabase
            .from('user_profiles')
            .update({
              national_id: cleanedNationalId,
              national_id_photo_url: publicUrl,
            })
            .eq('id', memberUserId);

          if (profileUpdateError) {
            throw profileUpdateError;
          }
        } catch (photoErr: any) {
          console.error('Existing member added, but ID photo upload failed:', photoErr);
          alert(
            `Member ${memberFullName || 'User'} was added, but ID photo upload failed: ${photoErr?.message || 'Unknown error'}.`
          );
        }
      }

      // Refresh member list
      if (currentGroupId) {
        await loadGroupDetails(currentGroupId);
      }

      // Show success message only if not bulk import (to avoid spam during CSV import)
      // During bulk import, suppress ALL alerts - they will be shown as a summary at the end
      if (!isBulkImport) {
        alert(
          createdNewUser
            ? `Member ${memberFullName || 'User'} has been successfully registered and added to the group.`
            : `Member ${memberFullName || 'User'} has been successfully added to the group.`
        );
      } else {
        // During bulk import, silently succeed (no alert)
        // Progress and summary will be shown in the import modal
        console.log(`[Bulk Import] Successfully processed: ${memberFullName || 'User'} (suppressing alert)`);
      }
    } catch (err: any) {
      console.error('Error registering member:', err);
      setError(err.message || 'Failed to register member. Please try again.');
      throw err; // Re-throw to let modal handle it
    } finally {
      setIsRegisteringMember(false);
    }
  };

  const handleImportMembers = async (rows: any[], onProgress?: (progress: { current: number; total: number; percentage: number }) => void) => {
    if (!currentGroupId) return;
    
    setIsImporting(true);
    setError(null);
    let successCount = 0;
    let failCount = 0;
    const errors: string[] = [];
    const totalRows = rows.length;

    // Helper function to wait/delay
    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    // Helper function to retry with exponential backoff
    const retryWithBackoff = async (
      fn: () => Promise<any>,
      maxRetries: number = 3,
      baseDelay: number = 1000
    ): Promise<any> => {
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          return await fn();
        } catch (err: any) {
          const isRateLimit = err.message?.toLowerCase().includes('rate limit') || 
                             err.message?.toLowerCase().includes('too many requests') ||
                             err.code === 'PGRST301' || 
                             err.statusCode === 429;
          
          if (isRateLimit && attempt < maxRetries - 1) {
            const delayMs = baseDelay * Math.pow(2, attempt); // Exponential backoff: 1s, 2s, 4s
            console.log(`Rate limit hit, retrying in ${delayMs}ms (attempt ${attempt + 1}/${maxRetries})`);
            await delay(delayMs);
            continue;
          }
          throw err;
        }
      }
    };

    try {
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const current = i + 1;
        const percentage = Math.round((current / totalRows) * 100);
        
        // Update progress
        if (onProgress) {
          onProgress({ current, total: totalRows, percentage });
        }
        
        try {
          // Process each row
          // role should be 'admin', 'member', or 'clerk'
          const role = (row.role || 'member').toLowerCase() as 'admin' | 'member' | 'clerk';
          
          // Retry with backoff for rate limit errors
          // IMPORTANT: Pass isBulkImport=true to suppress individual alerts during bulk import
          await retryWithBackoff(async () => {
            await handleRegisterMember(
              row.national_id,
              row.password || 'Saving@123', // Default password if missing
              row.full_name,
              role,
              row.phone || undefined,
              undefined, // No photo for bulk import
              true // isBulkImport = true (suppresses individual alerts)
            );
          });
          
          successCount++;
          
          // Add delay between requests to avoid rate limiting (500ms between each user)
          // Skip delay on last item
          if (i < rows.length - 1) {
            await delay(500);
          }
        } catch (err: any) {
          console.error(`Failed to import row ${row.lineNumber}:`, err);
          failCount++;
          
          // Provide more specific error messages
          let errorMsg = err.message || 'Unknown error';
          
          // Check for specific error types
          if (errorMsg.toLowerCase().includes('national id already exists') || 
              errorMsg.toLowerCase().includes('already registered') ||
              errorMsg.toLowerCase().includes('already exists')) {
            errorMsg = 'A user with this National ID already exists';
          } else if (errorMsg.toLowerCase().includes('database error') ||
                     errorMsg.toLowerCase().includes('failed to create') ||
                     errorMsg.toLowerCase().includes('profile creation failed')) {
            errorMsg = 'Database error saving new user';
          } else if (errorMsg.toLowerCase().includes('rate limit') || 
                     errorMsg.toLowerCase().includes('too many requests') ||
                     err.code === 'PGRST301' || 
                     err.statusCode === 429) {
            errorMsg = 'Request rate limit reached';
          }
          
          errors.push(`Row ${row.lineNumber} (${row.full_name}): ${errorMsg}`);
          
          // If rate limit error, add longer delay before next attempt
          const isRateLimit = errorMsg.toLowerCase().includes('rate limit') || 
                             errorMsg.toLowerCase().includes('too many requests') ||
                             err.code === 'PGRST301' || 
                             err.statusCode === 429;
          
          if (isRateLimit && i < rows.length - 1) {
            console.log('Rate limit detected, waiting 2 seconds before next row...');
            await delay(2000);
          }
        }
      }

      // Final progress update
      if (onProgress) {
        onProgress({ current: totalRows, total: totalRows, percentage: 100 });
      }

      // Show single summary message (no alert, just set error/success state)
      if (successCount > 0 && failCount === 0) {
        // All succeeded - show success message
        setError(null);
        // Success will be shown in modal via importProgress state
      } else if (successCount > 0 && failCount > 0) {
        // Partial success
        const summary = `✅ Successfully imported ${successCount} member(s).\n❌ ${failCount} failed.\n\nErrors:\n${errors.slice(0, 10).join('\n')}${errors.length > 10 ? `\n... and ${errors.length - 10} more errors` : ''}`;
        setError(summary);
      } else {
        // All failed
        const summary = `❌ Import failed for all ${failCount} member(s).\n\nErrors:\n${errors.slice(0, 10).join('\n')}${errors.length > 10 ? `\n... and ${errors.length - 10} more errors` : ''}`;
        setError(summary);
      }
    } finally {
      setIsImporting(false);
      if (currentGroupId) {
        await loadGroupDetails(currentGroupId);
      }
    }
  };

  const handleCreateGroupSuccess = () => {
    // Refresh the page to load new group data
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50 relative">
      {/* Nasuman Logo Watermark Background */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center opacity-[0.03]">
          <svg className="w-[800px] h-[800px]" viewBox="0 0 1408 736" xmlns="http://www.w3.org/2000/svg">
            {/* Flowing Waves and Basket Pattern */}
            <g>
              {/* Left Wave */}
              <path d="M 350 280 Q 400 200 450 250 Q 480 280 470 320 Q 460 360 420 390 Q 380 420 350 400 Q 320 380 310 340 Q 300 300 320 270 Q 330 250 350 280"
                fill="none" stroke="currentColor" strokeWidth="8" opacity="0.6" />

              {/* Right Wave */}
              <path d="M 950 280 Q 920 250 930 270 Q 940 300 950 340 Q 960 380 980 400 Q 1010 420 1040 390 Q 1070 360 1060 320 Q 1050 280 1020 250 Q 990 200 950 280"
                fill="none" stroke="currentColor" strokeWidth="8" opacity="0.6" />

              {/* Center Circular Wave */}
              <circle cx="650" cy="300" r="180" fill="none" stroke="currentColor" strokeWidth="10" opacity="0.5" />
              <circle cx="650" cy="300" r="200" fill="none" stroke="currentColor" strokeWidth="6" opacity="0.4" />
              <circle cx="650" cy="300" r="220" fill="none" stroke="currentColor" strokeWidth="4" opacity="0.3" />

              {/* Basket Weave Pattern in Center */}
              <g transform="translate(650, 300)">
                {/* Vertical Weave */}
                <path d="M -40 -60 L -40 60" stroke="currentColor" strokeWidth="4" opacity="0.5" />
                <path d="M -20 -60 L -20 60" stroke="currentColor" strokeWidth="4" opacity="0.5" />
                <path d="M 0 -60 L 0 60" stroke="currentColor" strokeWidth="4" opacity="0.5" />
                <path d="M 20 -60 L 20 60" stroke="currentColor" strokeWidth="4" opacity="0.5" />
                <path d="M 40 -60 L 40 60" stroke="currentColor" strokeWidth="4" opacity="0.5" />

                {/* Horizontal Weave */}
                <path d="M -60 -40 L 60 -40" stroke="currentColor" strokeWidth="4" opacity="0.5" />
                <path d="M -60 -20 L 60 -20" stroke="currentColor" strokeWidth="4" opacity="0.5" />
                <path d="M -60 0 L 60 0" stroke="currentColor" strokeWidth="4" opacity="0.5" />
                <path d="M -60 20 L 60 20" stroke="currentColor" strokeWidth="4" opacity="0.5" />
                <path d="M -60 40 L 60 40" stroke="currentColor" strokeWidth="4" opacity="0.5" />

                {/* Lock Icon */}
                <rect x="-20" y="-10" width="40" height="30" rx="5" fill="currentColor" opacity="0.4" />
                <circle cx="0" cy="-20" r="12" fill="none" stroke="currentColor" strokeWidth="4" opacity="0.4" />
              </g>

              {/* Decorative Butterflies/Birds */}
              <g transform="translate(800, 180)">
                <path d="M 0 0 Q -15 -10 -20 -5 Q -15 0 0 5" fill="currentColor" opacity="0.3" />
                <path d="M 0 0 Q 15 -10 20 -5 Q 15 0 0 5" fill="currentColor" opacity="0.3" />
              </g>
              <g transform="translate(500, 180)">
                <path d="M 0 0 Q -15 -10 -20 -5 Q -15 0 0 5" fill="currentColor" opacity="0.3" />
                <path d="M 0 0 Q 15 -10 20 -5 Q 15 0 0 5" fill="currentColor" opacity="0.3" />
              </g>

              {/* Bottom Flowing Waves */}
              <path d="M 200 450 Q 400 420 600 450 Q 800 480 1000 450"
                fill="none" stroke="currentColor" strokeWidth="12" strokeLinecap="round" opacity="0.4" />
              <path d="M 250 480 Q 450 450 650 480 Q 850 510 1050 480"
                fill="none" stroke="currentColor" strokeWidth="10" strokeLinecap="round" opacity="0.3" />
            </g>
          </svg>
        </div>
      </div>

      {/* Vanuatu Sand Pattern Strip */}
      <div className="fixed top-0 left-0 right-0 w-full h-3 bg-gradient-to-r from-blue-600 to-emerald-600 z-50">
        <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <defs>
            <pattern id="sandPatternTop" x="0" y="0" width="80" height="12" patternUnits="userSpaceOnUse">
              <line x1="0" y1="6" x2="15" y2="6" stroke="white" strokeWidth="0.5" opacity="0.4" />
              <line x1="20" y1="3" x2="35" y2="3" stroke="white" strokeWidth="0.5" opacity="0.3" />
              <line x1="20" y1="9" x2="35" y2="9" stroke="white" strokeWidth="0.5" opacity="0.3" />
              <line x1="40" y1="6" x2="55" y2="6" stroke="white" strokeWidth="0.5" opacity="0.4" />
              <line x1="60" y1="4" x2="70" y2="4" stroke="white" strokeWidth="0.5" opacity="0.3" />
              <line x1="60" y1="8" x2="70" y2="8" stroke="white" strokeWidth="0.5" opacity="0.3" />
              <circle cx="75" cy="6" r="0.8" fill="white" opacity="0.3" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#sandPatternTop)" />
        </svg>
      </div>

      <Header
        onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        isMobileMenuOpen={isMobileMenuOpen} />


      <div className="flex">
        <div className="hidden lg:block">
          <Sidebar
            isCollapsed={isSidebarCollapsed}
            userRole={userProfile?.role === 'super_admin' || userProfile?.role === 'group_admin' ? 'admin' : 'member'}
            isSuperAdmin={isSuperAdminUser}
          />
        </div>

        <main
          className={`flex-1 transition-layout ${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`
          }>

          <div className="p-6 max-w-7xl mx-auto">
            <div className="mb-6">
              <BreadcrumbTrail />
            </div>

            {/* Hero Section with Blue Gradient */}
            <div className="mb-8 relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 to-blue-700 p-8 shadow-xl">
              {/* Sand Drawing Pattern Background */}
              <div className="absolute inset-0 opacity-10">
                <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
                  {/* Vanuatu Spiral Pattern */}
                  <g stroke="white" strokeWidth="2" fill="none" strokeLinecap="round">
                    <path d="M 100,60 Q 120,40 140,60 Q 160,80 140,100 Q 120,120 100,100 Q 80,80 100,60" opacity="0.6" />
                    <path d="M 90,50 Q 120,20 150,50 Q 180,80 150,110 Q 120,140 90,110 Q 60,80 90,50" opacity="0.5" />
                  </g>
                  {/* Interlocking Pattern */}
                  <g stroke="white" strokeWidth="2" fill="none">
                    <path d="M 300,40 L 350,140 L 250,140 Z" opacity="0.5" />
                    <path d="M 350,140 L 400,40 L 450,140 Z" opacity="0.5" />
                  </g>
                  {/* Wave-style Strokes */}
                  <g stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.4">
                    <path d="M 500,60 Q 520,50 540,60 Q 560,70 580,60" />
                    <path d="M 500,80 Q 520,70 540,80 Q 560,90 580,80" />
                    <path d="M 500,100 Q 520,90 540,100 Q 560,110 580,100" />
                  </g>
                  {/* Decorative Circles */}
                  <g fill="white" opacity="0.3">
                    <circle cx="700" cy="70" r="3" />
                    <circle cx="720" cy="90" r="2" />
                    <circle cx="740" cy="70" r="2.5" />
                  </g>
                </svg>
              </div>
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMC41IiBvcGFjaXR5PSIwLjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-20"></div>
              <div className="relative z-10 flex items-center justify-between">
                <div>
                  <h1 className="text-4xl font-bold text-white mb-3 drop-shadow-md">
                    Group Management
                  </h1>
                  <p className="text-blue-100 text-lg">
                    Configure group settings, manage members, and control contribution schedules
                  </p>
                </div>
                {canCreateGroup && (
                  <Button
                    variant="default"
                    iconName="Plus"
                    iconPosition="left"
                    onClick={() => setIsCreateModalOpen(true)}
                    className="bg-white text-blue-600 hover:bg-blue-50 shadow-lg"
                  >
                    Create Group
                  </Button>
                )}
              </div>
            </div>

            {accessLevel.payment_overdue && currentGroupId && (
              <PaymentWarningBanner
                message={accessLevel.reason || 'Payment is overdue'}
                paymentDueDate={accessLevel.payment_due_date}
                variant="error"
              />
            )}



            {error && (
              <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <p className="text-destructive text-sm">{error}</p>
              </div>
            )}

            {/* Group list view for admins with multiple groups */}
            {showGroupListForAdmin ? (
              <div className="space-y-6">
                <div className="bg-white rounded-2xl border border-blue-100 shadow-xl p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                      <Icon name="Users" size={24} className="text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Your Groups</h2>
                      <p className="text-sm text-gray-600">
                        Select a group to view and manage its details
                      </p>
                    </div>
                  </div>

                  <div className="overflow-x-auto -mx-8">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b-2 border-blue-100 bg-gradient-to-r from-blue-50 to-transparent">
                          <th className="text-left py-4 px-6 text-sm font-semibold text-blue-900">Group Name</th>
                          <th className="text-left py-4 px-6 text-sm font-semibold text-blue-900">Description</th>
                          <th className="text-left py-4 px-6 text-sm font-semibold text-blue-900">Status</th>
                          <th className="text-left py-4 px-6 text-sm font-semibold text-blue-900">Created</th>
                          <th className="text-right py-4 px-6 text-sm font-semibold text-blue-900">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allGroups.map((group) => {
                          // Debug: Log full group object to see what data we have
                          console.log(`🔴 [RENDER] Group "${group.name}":`, {
                            id: group.id,
                            createdBy: group.createdBy,
                            creatorCompanyLogoUrl: group.creatorCompanyLogoUrl,
                            companyLogoUrl: group.companyLogoUrl,
                            companyName: group.companyName
                          });
                          
                          // Determine which logo to use
                          const logoUrl = (group.creatorCompanyLogoUrl && group.creatorCompanyLogoUrl.trim() !== '') 
                            ? group.creatorCompanyLogoUrl 
                            : (group.companyLogoUrl && group.companyLogoUrl.trim() !== '') 
                              ? group.companyLogoUrl 
                              : '/assets/images/ihslogo.png';
                          
                          // Debug: Log to console for this specific group
                          if (logoUrl !== '/assets/images/ihslogo.png') {
                            console.log(`[RENDER] Group "${group.name}": Using logo:`, logoUrl);
                          } else {
                            console.log(`[RENDER] Group "${group.name}": Using default logo. creatorCompanyLogoUrl:`, group.creatorCompanyLogoUrl, 'companyLogoUrl:', group.companyLogoUrl);
                          }
                          
                          return (
                            <tr key={group.id} className="border-b border-gray-100 hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-transparent transition-all duration-200">
                              <td className="py-4 px-6">
                                <div className="flex items-center gap-3">
                                  <img
                                    src={logoUrl}
                                    alt={group.name}
                                    className="w-10 h-10 rounded-lg object-cover border border-gray-200 shadow-md"
                                    onError={(e) => {
                                      const target = e.target as HTMLImageElement;
                                      console.error(`[RENDER] Image failed to load for group "${group.name}":`, target.src);
                                      target.src = '/assets/images/ihslogo.png';
                                    }}
                                    onLoad={() => {
                                      if (logoUrl !== '/assets/images/ihslogo.png') {
                                        console.log(`[RENDER] ✓ Logo loaded for group "${group.name}":`, logoUrl);
                                      }
                                    }}
                                  />
                                  <div className="flex flex-col">
                                    <span className="text-sm text-gray-900 font-semibold">{group.name}</span>
                                    {group.companyName && group.companyName !== 'Nasuman' && (
                                      <span className="text-xs text-gray-500 mt-0.5">{group.companyName}</span>
                                    )}
                                  </div>
                                </div>
                              </td>
                            <td className="py-4 px-6 text-sm text-gray-600">{group.description || 'No description'}</td>
                            <td className="py-4 px-6">
                              <span
                                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                                  group.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'
                                }`}
                              >
                                <span className={`w-1.5 h-1.5 rounded-full ${group.isActive ? 'bg-emerald-500' : 'bg-gray-400'}`}></span>
                                {group.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="py-4 px-6 text-sm text-gray-600">
                              {new Date(group.createdAt).toLocaleDateString()}
                            </td>
                            <td className="py-4 px-6 text-right">
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => setSelectedGroupId(group.id)}
                                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-md"
                              >
                                View Details
                              </Button>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Group selector for admins (when viewing details) */}
            {!loading && !isSuperAdminUser && allGroups.length > 1 && selectedGroupId && (
              <div className="mb-6 bg-white rounded-2xl border border-blue-100 shadow-sm p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">Select Group</div>
                    <div className="text-xs text-gray-600">You have access to multiple groups. Choose which one to manage.</div>
                  </div>
                  <div className="min-w-[260px]">
                    <select
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={selectedGroupId || ''}
                      onChange={(e) => setSelectedGroupId(e.target.value)}
                    >
                      {allGroups.map((g) => (
                        <option key={g.id} value={g.id}>
                          {g.name}{g.companyName ? ` - ${g.companyName}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {showGroupListForAdmin ? null : loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-muted-foreground">Loading group data...</div>
              </div>
            ) : isSuperAdminUser && allGroups.length > 0 && !selectedGroupId && activeSection !== 'members' ? (
              <div className="space-y-6">
                <div className="bg-white rounded-2xl border border-blue-100 shadow-xl p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                      <Icon name="Users" size={24} className="text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">All Groups</h2>
                      <p className="text-sm text-gray-600">
                        Select a group to view and manage its details
                      </p>
                    </div>
                  </div>
                  <div className="overflow-x-auto -mx-8">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b-2 border-blue-100 bg-gradient-to-r from-blue-50 to-transparent">
                          <th className="text-left py-4 px-6 text-sm font-semibold text-blue-900">Group Name</th>
                          <th className="text-left py-4 px-6 text-sm font-semibold text-blue-900">Description</th>
                          <th className="text-left py-4 px-6 text-sm font-semibold text-blue-900">Creator</th>
                          <th className="text-left py-4 px-6 text-sm font-semibold text-blue-900">Members</th>
                          <th className="text-left py-4 px-6 text-sm font-semibold text-blue-900">Status</th>
                          <th className="text-left py-4 px-6 text-sm font-semibold text-blue-900">Created</th>
                          <th className="text-right py-4 px-6 text-sm font-semibold text-blue-900">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allGroups.map((group, index) => (
                          <tr key={group.id} className="border-b border-gray-100 hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-transparent transition-all duration-200">
                            <td className="py-4 px-6">
                              <div className="flex items-center gap-3">
                                <img
                                  src={
                                    (group.creatorCompanyLogoUrl && group.creatorCompanyLogoUrl.trim() !== '') 
                                      ? group.creatorCompanyLogoUrl 
                                      : (group.companyLogoUrl && group.companyLogoUrl.trim() !== '') 
                                        ? group.companyLogoUrl 
                                        : '/assets/images/ihslogo.png'
                                  }
                                  alt={group.name}
                                  className="w-10 h-10 rounded-lg object-cover border border-gray-200 shadow-md"
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.src = '/assets/images/ihslogo.png';
                                  }}
                                />
                                <div className="flex flex-col">
                                  <span className="text-sm text-gray-900 font-semibold">{group.name}</span>
                                  {group.companyName && group.companyName !== 'Nasuman' && (
                                    <span className="text-xs text-gray-500 mt-0.5">{group.companyName}</span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="py-4 px-6 text-sm text-gray-600">{group.description || 'No description'}</td>
                            <td className="py-4 px-6 text-sm text-gray-700">{group.creatorName || 'Unknown'}</td>
                            <td className="py-4 px-6">
                              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-sm font-medium">
                                <Icon name="Users" size={14} />
                                {group.memberCount}
                              </span>
                            </td>
                            <td className="py-4 px-6">
                              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${group.isActive
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'bg-gray-100 text-gray-600'
                                }`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${group.isActive ? 'bg-emerald-500' : 'bg-gray-400'}`}></span>
                                {group.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="py-4 px-6 text-sm text-gray-600">
                              {new Date(group.createdAt).toLocaleDateString()}
                            </td>
                            <td className="py-4 px-6 text-right">
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => {
                                  setSelectedGroupId(group.id);
                                  loadGroupDetails(group.id);
                                }}
                                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-md"
                              >
                                View Details
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : !currentGroupId && !isSuperAdminUser && allGroups.length === 0 ? (
              <div className="flex items-center justify-center py-16">
                <div className="text-center max-w-md">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                    <Icon name="Users" size={40} className="text-blue-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">No Group Found</h3>
                  <p className="text-gray-600 mb-6">Create your first group to start managing members and contributions.</p>
                  {canCreateGroup && (
                    <Button
                      variant="default"
                      iconName="Plus"
                      iconPosition="left"
                      onClick={() => setIsCreateModalOpen(true)}
                      className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg"
                    >
                      Create Group
                    </Button>
                  )}
                </div>
              </div>
            ) : activeSection === 'members' ? (
              // Members page - focused view
              <div className="space-y-6">
                {selectedGroupId && allGroups.length > 1 && (
                  <div className="mb-4">
                    <Button
                      variant="outline"
                      iconName="ArrowLeft"
                      iconPosition="left"
                      onClick={() => {
                        setSelectedGroupId(null);
                        setCurrentGroupId(null);
                        window.location.hash = '';
                      }}
                    >
                      {isSuperAdminUser ? 'Back to All Groups' : 'Back to Group List'}
                    </Button>
                  </div>
                )}

                <div className="mb-8">
                  <h1 className="text-3xl font-semibold text-foreground mb-2">
                    Member Management
                  </h1>
                  <p className="text-muted-foreground">
                    Create, manage, and remove group members
                  </p>
                </div>

                {accessLevel.payment_overdue && currentGroupId && (
                  <PaymentWarningBanner
                    message={accessLevel.reason || 'Payment is overdue'}
                    paymentDueDate={accessLevel.payment_due_date}
                    variant="error"
                  />
                )}

                {/* Member Management - Focused view */}
                {currentGroupId ? (
                  <MemberManagementTable
                    members={members}
                    onRemoveMember={handleRemoveMember}
                    onChangeRole={handleChangeRole}
                    onRegisterMember={() => setIsRegisterMemberModalOpen(true)}
                    onImportMembers={() => setIsImportModalOpen(true)}
                    onPayMembershipFee={handlePayMembershipFee}
                    groupMembershipFee={groupSettings.membershipFee}
                    groupCreatorId={groupSettings.createdBy} />
                ) : (
                  <div className="bg-card rounded-md border border-border shadow-elevation-1 p-6">
                    {isSuperAdminUser && allGroups.length > 0 ? (
                      <div className="space-y-4">
                        <div className="text-center py-2">
                          <Icon name="Users" size={40} className="text-muted-foreground mx-auto mb-3" />
                          <h3 className="text-lg font-semibold text-foreground mb-1">Select a Group</h3>
                          <p className="text-muted-foreground text-sm">
                            Choose a group to manage its members.
                          </p>
                        </div>

                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-muted/50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                  Group
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                  Members
                                </th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                  Action
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                              {allGroups.map((group) => (
                                <tr key={group.id} className="hover:bg-muted/30">
                                  <td className="px-4 py-3 text-sm text-foreground">
                                    <div className="flex items-center gap-3">
                                      <img
                                        src={group.creatorCompanyLogoUrl || group.companyLogoUrl || '/assets/images/ihslogo.png'}
                                        alt={group.name}
                                        className="w-8 h-8 rounded-lg object-cover border border-border"
                                        onError={(e) => {
                                          const target = e.target as HTMLImageElement;
                                          target.src = '/assets/images/ihslogo.png';
                                        }}
                                      />
                                      <div className="flex flex-col">
                                        <div className="font-medium">{group.name}</div>
                                        {group.companyName && group.companyName !== 'Nasuman' && (
                                          <div className="text-xs text-muted-foreground mt-0.5">{group.companyName}</div>
                                        )}
                                        <div className="text-xs text-muted-foreground">
                                          {group.description || 'No description'}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-sm text-foreground">
                                    {group.memberCount}
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    <Button
                                      variant="default"
                                      size="sm"
                                      onClick={() => {
                                        setSelectedGroupId(group.id);
                                        loadGroupDetails(group.id);
                                        window.location.hash = 'members';
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                      }}
                                    >
                                      Manage Members
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-foreground mb-2">Member Management</h3>
                        <p className="text-muted-foreground mb-4">
                          {loading ? 'Loading group information...' : 'No group selected. Please select or create a group.'}
                        </p>
                        {canCreateGroup && (
                          <Button
                            variant="default"
                            iconName="Plus"
                            iconPosition="left"
                            onClick={() => setIsCreateModalOpen(true)}
                          >
                            Create Group
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                {selectedGroupId && allGroups.length > 1 && (
                  <div className="mb-4">
                    <Button
                      variant="outline"
                      iconName="ArrowLeft"
                      iconPosition="left"
                      onClick={() => {
                        setSelectedGroupId(null);
                        setCurrentGroupId(null);
                      }}
                    >
                      {isSuperAdminUser ? 'Back to All Groups' : 'Back to Group List'}
                    </Button>
                  </div>
                )}
                <GroupInfoCard
                  groupSettings={groupSettings}
                  onUpdate={handleUpdateGroupSettings} />

                <SubscriptionManagement groupId={currentGroupId} />

                <ContributionScheduleCard
                  schedule={contributionSchedule}
                  onUpdate={handleUpdateSchedule} />

                <ContributionSettingsCard
                  settings={contributionSettings}
                  onUpdate={handleUpdateContributionSettings} />

                {/* Loan Settings */}
                {currentGroupId && (
                  <LoanSettingsCard
                    groupId={currentGroupId}
                    accessLevel={accessLevel}
                  />
                )}

                {/* Member Management - Always show for admins when group is selected */}
                {currentGroupId ? (
                  <MemberManagementTable
                    members={members}
                    onRemoveMember={handleRemoveMember}
                    onChangeRole={handleChangeRole}
                    onRegisterMember={() => setIsRegisterMemberModalOpen(true)}
                    onImportMembers={() => setIsImportModalOpen(true)}
                    onPayMembershipFee={handlePayMembershipFee}
                    groupMembershipFee={groupSettings.membershipFee}
                    groupCreatorId={groupSettings.createdBy} />
                ) : (
                  <div className="bg-card rounded-md border border-border shadow-elevation-1 p-6">
                    <div className="text-center py-8">
                      <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-foreground mb-2">Member Management</h3>
                      <p className="text-muted-foreground mb-4">
                        {loading ? 'Loading group information...' : 'No group selected. Please select or create a group.'}
                      </p>
                    </div>
                  </div>
                )}

                <VerificationSettingsCard
                  settings={verificationSettings}
                  onUpdate={setVerificationSettings} />


                <AdvancedSettingsCard
                  conflictSettings={conflictSettings}
                  exportPermissions={exportPermissions}
                  notificationSettings={notificationSettings}
                  onUpdateConflict={setConflictSettings}
                  onUpdateExport={setExportPermissions}
                  onUpdateNotifications={setNotificationSettings} />
              </div>
            )}
          </div>
        </main>
      </div>

      <button
        onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        className="hidden lg:flex fixed bottom-6 left-6 w-10 h-10 bg-primary text-primary-foreground rounded-full items-center justify-center shadow-elevation-2 hover-scale z-1000"
        aria-label="Toggle sidebar">

        <span className="text-sm font-medium">
          {isSidebarCollapsed ? '→' : '←'}
        </span>
      </button>

      {canCreateGroup && (
        <CreateGroupModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={handleCreateGroupSuccess}
        />
      )}

      <RegisterMemberModal
        isOpen={isRegisterMemberModalOpen}
        onClose={() => setIsRegisterMemberModalOpen(false)}
        onRegister={handleRegisterMember}
        isLoading={isRegisteringMember}
      />

      <ImportMembersModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImport={handleImportMembers}
        isLoading={isImporting}
      />
    </div>);

};

export default GroupManagement;